import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useNotification } from './NotificationContext';

interface AuthUser {
  id: string;
  email: string;
}

interface AuthContextType {
  user: AuthUser | null;
  isAdmin: boolean;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  openLoginModal: () => void;
  closeLoginModal: () => void;
  isLoginModalOpen: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification } = useNotification();

  useEffect(() => {
    const initAuth = async () => {
      try {
        // Check if we have a valid session first
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('Session error:', sessionError);
          setLoading(false);
          return;
        }

        if (!session) {
          setLoading(false);
          return;
        }

        console.log("Auth context - session found:", session.user.email);

        // Check if user is admin
        const { data: permissions, error: permError } = await supabase
          .from('user_permissions')
          .select('is_admin')
          .eq('user_id', session.user.id)
          .maybeSingle();

        if (permError) {
          console.error('Error checking permissions:', permError);
          // Continue anyway, might be a new user
        }

        setUser({
          id: session.user.id,
          email: session.user.email!
        });
        
        setIsAdmin(permissions?.is_admin || false);
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        setLoading(false);
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (session) {
        console.log("Auth state change - session found:", session.user.email);
        
        // Check if user is admin
        const { data: permissions, error: permError } = await supabase
          .from('user_permissions')
          .select('is_admin')
          .eq('user_id', session.user.id)
          .maybeSingle();

        if (permError) {
          console.error('Error checking permissions:', permError);
          // Continue anyway, might be a new user
        }

        setUser({
          id: session.user.id,
          email: session.user.email!
        });
        
        setIsAdmin(permissions?.is_admin || false);
      } else {
        setUser(null);
        setIsAdmin(false);
      }
      setLoading(false);
    });

    initAuth();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signInWithGoogle = async () => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      });

      if (error) throw error;
      
      // The redirect will happen automatically
    } catch (error) {
      console.error('Sign in error:', error);
      const message = error instanceof Error ? error.message : 'Failed to sign in with Google';
      showNotification('error', message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signInWithEmail = async (email: string, password: string) => {
    try {
      setLoading(true);
      
      // Validate email format
      if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
        throw new Error('Invalid email format');
      }

      // Validate password (minimum requirements)
      if (password.length < 6) {
        throw new Error('Password should be at least 6 characters long');
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        // Map Supabase errors to user-friendly messages
        if (error.message.includes('Invalid login credentials')) {
          throw new Error('Invalid email or password');
        } else if (error.message.includes('Email not confirmed')) {
          throw new Error('Please verify your email address');
        }
        throw error;
      }
      
      // Check if user is admin
      const { data: permissions } = await supabase
        .from('user_permissions')
        .select('is_admin')
        .eq('user_id', data.user.id)
        .maybeSingle();
      
      setIsAdmin(permissions?.is_admin || false);
      
      showNotification('success', 'Successfully signed in');
    } catch (error) {
      console.error('Sign in error:', error);
      const message = error instanceof Error ? error.message : 'Failed to sign in';
      showNotification('error', message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      setUser(null);
      setIsAdmin(false);
      showNotification('success', 'Successfully signed out');
      navigate('/');
    } catch (error) {
      console.error('Sign out error:', error);
      const message = error instanceof Error ? error.message : 'Failed to sign out';
      showNotification('error', message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const openLoginModal = () => {
    setIsLoginModalOpen(true);
  };

  const closeLoginModal = () => {
    setIsLoginModalOpen(false);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      isAdmin,
      loading, 
      signInWithGoogle,
      signInWithEmail,
      signOut, 
      openLoginModal, 
      closeLoginModal, 
      isLoginModalOpen 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}